/**
 * Graph layout algorithm for ResourceGraph.
 *
 * Computes a hierarchical (tree) layout from an array of PulumiResource
 * objects with parent-child relationships.  The algorithm:
 *
 * 1. Builds a parent-child adjacency map.
 * 2. Assigns depth levels via BFS from root nodes.
 * 3. Groups nodes by level and assigns (x, y) positions.
 * 4. Centers each level horizontally.
 * 5. Resolves edges to pixel coordinates.
 */

import type {
  PulumiResource,
  GraphNode,
  GraphEdge,
  ResolvedEdge,
  LayoutConfig,
} from "./types";

// ---------------------------------------------------------------------------
// Default configuration
// ---------------------------------------------------------------------------

export const DEFAULT_LAYOUT_CONFIG: LayoutConfig = {
  nodeWidth: 180,
  nodeHeight: 60,
  horizontalGap: 40,
  verticalGap: 80,
  padding: 40,
};

// ---------------------------------------------------------------------------
// Layout result
// ---------------------------------------------------------------------------

export interface LayoutResult {
  /** Map from URN to positioned GraphNode */
  nodes: Map<string, GraphNode>;
  /** Parent-child and dependency edges with raw URNs */
  edges: GraphEdge[];
  /** Edges resolved to pixel coordinates */
  resolvedEdges: ResolvedEdge[];
  /** Total width of the laid-out graph */
  width: number;
  /** Total height of the laid-out graph */
  height: number;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Compute a hierarchical layout for the given resources.
 */
export function computeLayout(
  resources: PulumiResource[],
  config: LayoutConfig = DEFAULT_LAYOUT_CONFIG,
): LayoutResult {
  if (resources.length === 0) {
    return { nodes: new Map(), edges: [], resolvedEdges: [], width: 0, height: 0 };
  }

  const urnSet = new Set(resources.map((r) => r.urn));

  // ----- 1. Build adjacency maps -----
  const childrenMap = new Map<string, string[]>();
  const parentMap = new Map<string, string>();

  for (const r of resources) {
    if (r.parent && urnSet.has(r.parent)) {
      parentMap.set(r.urn, r.parent);
      const siblings = childrenMap.get(r.parent) ?? [];
      siblings.push(r.urn);
      childrenMap.set(r.parent, siblings);
    }
  }

  // ----- 2. Find root nodes -----
  const rootUrns: string[] = [];
  for (const r of resources) {
    if (!parentMap.has(r.urn)) {
      rootUrns.push(r.urn);
    }
  }

  // ----- 3. BFS to assign levels -----
  const levels = new Map<string, number>();
  const queue: Array<{ urn: string; level: number }> = rootUrns.map((urn) => ({
    urn,
    level: 0,
  }));

  while (queue.length > 0) {
    const { urn, level } = queue.shift()!;
    if (levels.has(urn)) continue;
    levels.set(urn, level);

    for (const child of childrenMap.get(urn) ?? []) {
      if (!levels.has(child)) {
        queue.push({ urn: child, level: level + 1 });
      }
    }
  }

  // Handle orphan nodes (no parent edge found in the set)
  for (const r of resources) {
    if (!levels.has(r.urn)) {
      levels.set(r.urn, 0);
    }
  }

  // ----- 4. Group by level -----
  const levelGroups = new Map<number, string[]>();
  let maxLevel = 0;

  for (const [urn, level] of levels) {
    maxLevel = Math.max(maxLevel, level);
    const group = levelGroups.get(level) ?? [];
    group.push(urn);
    levelGroups.set(level, group);
  }

  // ----- 5. Assign positions -----
  const resourceByUrn = new Map<string, PulumiResource>();
  for (const r of resources) {
    resourceByUrn.set(r.urn, r);
  }

  const nodes = new Map<string, GraphNode>();
  let graphWidth = 0;

  for (let level = 0; level <= maxLevel; level++) {
    const group = levelGroups.get(level) ?? [];
    const levelWidth =
      group.length * (config.nodeWidth + config.horizontalGap) - config.horizontalGap;
    graphWidth = Math.max(graphWidth, levelWidth);

    for (let col = 0; col < group.length; col++) {
      const urn = group[col];
      const resource = resourceByUrn.get(urn);
      if (!resource) continue;

      nodes.set(urn, {
        resource,
        x: col * (config.nodeWidth + config.horizontalGap) + config.padding,
        y: level * (config.nodeHeight + config.verticalGap) + config.padding,
        level,
        column: col,
        children: childrenMap.get(urn) ?? [],
      });
    }
  }

  // ----- 6. Center levels -----
  for (let level = 0; level <= maxLevel; level++) {
    const group = levelGroups.get(level) ?? [];
    const levelWidth =
      group.length * (config.nodeWidth + config.horizontalGap) - config.horizontalGap;
    const offset = (graphWidth - levelWidth) / 2;

    for (const urn of group) {
      const node = nodes.get(urn);
      if (node) {
        nodes.set(urn, { ...node, x: node.x + offset });
      }
    }
  }

  const totalWidth = graphWidth + config.padding * 2;
  const totalHeight =
    (maxLevel + 1) * (config.nodeHeight + config.verticalGap) -
    config.verticalGap +
    config.padding * 2;

  // ----- 7. Build edges -----
  const edges: GraphEdge[] = [];

  for (const r of resources) {
    // Parent edge
    if (r.parent && urnSet.has(r.parent)) {
      edges.push({ source: r.parent, target: r.urn, type: "parent" });
    }

    // Dependency edges (excluding parent to avoid duplicates)
    for (const dep of r.dependencies ?? []) {
      if (urnSet.has(dep) && dep !== r.parent) {
        edges.push({ source: dep, target: r.urn, type: "dependency" });
      }
    }
  }

  // ----- 8. Resolve edge coordinates -----
  const resolvedEdges = resolveEdgeCoordinates(edges, nodes, config);

  return { nodes, edges, resolvedEdges, width: totalWidth, height: totalHeight };
}

// ---------------------------------------------------------------------------
// Edge coordinate resolution
// ---------------------------------------------------------------------------

/**
 * Compute SVG pixel coordinates for each edge based on node positions.
 * Source connects at bottom-center, target connects at top-center.
 */
function resolveEdgeCoordinates(
  edges: GraphEdge[],
  nodes: Map<string, GraphNode>,
  config: LayoutConfig,
): ResolvedEdge[] {
  return edges
    .map((edge) => {
      const src = nodes.get(edge.source);
      const tgt = nodes.get(edge.target);
      if (!src || !tgt) return null;

      return {
        ...edge,
        sourceX: src.x + config.nodeWidth / 2,
        sourceY: src.y + config.nodeHeight,
        targetX: tgt.x + config.nodeWidth / 2,
        targetY: tgt.y,
      };
    })
    .filter((e): e is ResolvedEdge => e !== null);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract the cloud provider from a Pulumi resource type string.
 *
 * @example getProviderFromType("aws:s3/bucket:Bucket") => "aws"
 */
export function getProviderFromType(resourceType: string): string {
  const colon = resourceType.indexOf(":");
  if (colon > 0) {
    return resourceType.slice(0, colon).toLowerCase();
  }
  return "unknown";
}

/**
 * Extract a short display name from a Pulumi resource type.
 *
 * @example getShortType("aws:s3/bucket:Bucket") => "s3/Bucket"
 */
const FRIENDLY_TYPE_LABELS: Record<string, string> = {
  "data/DataTarget": "Data Target",
  "proxy/Instance": "Proxy",
  "gateway/Gateway": "Gateway",
  "core/Stack": "Stack",
  "core/Server": "Server",
};

export function getShortType(resourceType: string): string {
  const parts = resourceType.split(":");
  if (parts.length >= 3) {
    // e.g. ["aws", "s3/bucket", "Bucket"]
    const service = parts[1].split("/")[0];
    const name = parts[2];
    const key = `${service}/${name}`;
    return FRIENDLY_TYPE_LABELS[key] ?? key;
  }
  return FRIENDLY_TYPE_LABELS[resourceType] ?? resourceType;
}

/**
 * Provider color palettes used for the side-bar accent on nodes.
 */
export const PROVIDER_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  aws: { bg: "#FF9900", text: "#ffffff", border: "#FF9900" },
  azure: { bg: "#0078D4", text: "#ffffff", border: "#0078D4" },
  gcp: { bg: "#4285F4", text: "#ffffff", border: "#4285F4" },
  cloudflare: { bg: "#F6821F", text: "#ffffff", border: "#F6821F" },
  docker: { bg: "#2496ED", text: "#ffffff", border: "#2496ED" },
  pulumi: { bg: "#8A3FFC", text: "#ffffff", border: "#8A3FFC" },
  unknown: { bg: "#6B7280", text: "#ffffff", border: "#6B7280" },
};

/**
 * Status color mapping for resource nodes.
 */
export const STATUS_COLORS: Record<string, { fill: string; stroke: string; text: string }> = {
  created:   { fill: "#34D399", stroke: "#10B981", text: "#064E3B" },
  updated:   { fill: "#FBBF24", stroke: "#F59E0B", text: "#78350F" },
  deleted:   { fill: "#F87171", stroke: "#EF4444", text: "#7F1D1D" },
  unchanged: { fill: "#D1D5DB", stroke: "#9CA3AF", text: "#374151" },
  same:      { fill: "#D1D5DB", stroke: "#9CA3AF", text: "#374151" },
  creating:  { fill: "#60A5FA", stroke: "#3B82F6", text: "#1E3A8A" },
  updating:  { fill: "#FBBF24", stroke: "#F59E0B", text: "#78350F" },
  deleting:  { fill: "#F87171", stroke: "#EF4444", text: "#7F1D1D" },
  failed:    { fill: "#F87171", stroke: "#DC2626", text: "#FFFFFF" },
  pending:   { fill: "#9CA3AF", stroke: "#6B7280", text: "#1F2937" },
};

/**
 * Get status colors with a safe fallback.
 */
export function getStatusColors(status: string): { fill: string; stroke: string; text: string } {
  return STATUS_COLORS[status] ?? STATUS_COLORS.pending;
}
